/**
 * Learning Center API: the course editor, "Edit with AI"
 * (docs/plans/lms-course-editor-plan.md Appendix A and D, 4.6, 5.2, 6.2, 6.11,
 * 6.13; WP-E21's routes, WP-E04's client). Typed calls over services/api (the
 * shared axios instance with the JWT and X-Site-Id); nothing under src/lms
 * imports axios (LMS plan 2.3a). Every route sits behind the guard,
 * requireLmsAdmin, the builder switch, the network allow-list and the editor
 * switch (G25): 503 LMS_BUILDER_DISABLED while the builder switch is unset,
 * 503 LMS_EDITOR_DISABLED while the off-lever LMS_COURSE_EDITOR_ENABLED reads
 * 'false' (18.2 Q15; unset reads on), 403 LMS_BUILDER_NETWORK_NOT_ALLOWED
 * outside the allow-list, and 404 for a message or version of another
 * network. The screens read GET /health's builder.editor block before they
 * render an entry point.
 *
 *   GET  /admin/builder/versions/:versionId/edits?limit=&offset=      -> { items: message summaries, page }
 *   GET  /admin/builder/versions/:versionId/edits/estimate?section_ids= -> EditEstimate
 *   POST /admin/builder/versions/:versionId/edits (JSON or multipart)  -> 201 { message, run }
 *   GET  /admin/builder/edits/:messageId                              -> { message, run, sources, diff }
 *   POST /admin/builder/edits/:messageId/send                         -> { message, run }
 *   POST /admin/builder/edits/:messageId/cancel                       -> { message, run }
 *   POST /admin/builder/edits/:messageId/accept                       -> { message }
 *   POST /admin/builder/edits/:messageId/revert { reason? }           -> { message, version }
 *
 * Query strings travel as axios `params`, so the URL stays the path (the test
 * harness routes GETs by exact URL). A multipart body is a FormData posted with
 * no explicit Content-Type: the browser sets the boundary.
 */
import api from '../../services/api';
import type {
  ChecklistItem,
  CourseSource,
  GlossaryEntry,
  VersionQuizSettings,
} from '../content/types';
import { apiErrorCode } from './client';
import type { LmsList } from './client';
import { centsOf, readBuilderError } from './builder';
import type {
  BuilderApiError,
  BuilderExtraSource,
  BuilderProgress,
  BuilderRun,
  BuilderRunStatus,
  BuilderSource,
  BuilderSourceLink,
  BuilderSourceMeta,
  BuilderStage,
} from './builder';
import type { DiffReadingLine, DiffSection, VersionDetail } from './catalog';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Bounds (Appendix B defaults; the estimate route's limits block is the live
// value and the server re-checks every one of them)
// ---------------------------------------------------------------------------

/** LMS_EDITOR_MAX_INSTRUCTION_CHARS default: an instruction is 1 to 2000 characters after trimming. */
export const EDITOR_INSTRUCTION_MAX = 2000;
/** LMS_EDITOR_MAX_FILES_PER_MESSAGE default: files and links together. */
export const EDITOR_MAX_FILES_PER_MESSAGE = 4;
/** LMS_EDITOR_MAX_MESSAGES_PER_DRAFT default (429 LMS_EDITOR_MESSAGE_LIMIT beyond it). */
export const EDITOR_MAX_MESSAGES_PER_DRAFT = 30;
/** Appendix A: a revert reason is at most 500 characters. */
export const EDITOR_REVERT_REASON_MAX = 500;
/** Appendix A: a list page holds at most 200 rows. */
export const EDITOR_MESSAGES_MAX_LIMIT = 200;

// ---------------------------------------------------------------------------
// Shapes (5.2, Appendix A, Appendix D)
// ---------------------------------------------------------------------------

/** 5.2 lms_builder_edit_messages.outcome (NULL while the run is still working). */
export const EDIT_OUTCOMES = ['applied', 'no_change', 'clarify', 'declined', 'failed', 'cancelled'] as const;
export type EditOutcome = (typeof EDIT_OUTCOMES)[number];

/** 5.2 lms_builder_edit_messages.review: pending -> accepted -> reverted, or pending -> reverted (G26). */
export const EDIT_REVIEWS = ['pending', 'accepted', 'reverted'] as const;
export type EditReview = (typeof EDIT_REVIEWS)[number];

/** 5.2 scope.kind: the whole course, or chosen sections. */
export type EditScopeKind = 'course' | 'sections';

/** 5.2 lms_builder_edit_messages.scope (and targets.scope of the edit run, Appendix D). */
export interface EditScope {
  kind: EditScopeKind;
  /** Empty for kind 'course'. */
  section_ids: string[];
}

/** G22: the six named parts of a section the plan may name. */
export const EDIT_SECTION_PARTS = ['title', 'why_it_matters', 'slides', 'reading', 'what_to_remember', 'questions'] as const;
export type EditSectionPart = (typeof EDIT_SECTION_PARTS)[number];

/** G22: the five course-level fields the plan may name (whole-course scope only). */
export const EDIT_COURSE_FIELDS = [
  'learning_objectives',
  'not_covered',
  'glossary',
  'key_takeaways',
  'competency_checklist',
] as const;
export type EditCourseField = (typeof EDIT_COURSE_FIELDS)[number];

/** 5.2 plan_summary: what the plan named, in counts and names (never text beyond the change note). */
export interface EditPlanSummary {
  sections: Array<{ id: string; parts: EditSectionPart[] }>;
  added: number;
  course_level: EditCourseField[];
  swaps: number;
  stated_facts: number;
  change_note: string | null;
  /** 6.5: the re-priced figure after planning, when the pipeline records it here too. */
  planned_cents?: number | null;
}

/** 5.2 diff_summary and the diff artifact's counts (Appendix D). */
export interface EditDiffCounts {
  sections_changed: number;
  sections_added: number;
  slides_changed: number;
  paragraphs_changed: number;
  questions_changed: number;
  course_fields_changed: number;
  swaps_applied: number;
}

/** Appendix D warnings: the old wording of a stated fact still present after the edit (6.7). */
export interface EditResidueWarning {
  kind: 'residue';
  section_id: string | null;
  element_kind: string;
  element_ref: string;
  /** The index of the stated fact in the plan. */
  fact: number;
}

/** Appendix D warnings: the lint count rose (6.8). */
export interface EditLintRoseWarning {
  kind: 'lint_rose';
  before: number;
  after: number;
}

/** Appendix D warnings: the closing section was decided on (codes and ids only). */
export interface EditClosingDecisionWarning {
  kind: 'closing_decision';
  section_id: string;
}

/** 5.2 warnings: codes, ids and counts, never text (G26). */
export type EditWarning = EditResidueWarning | EditLintRoseWarning | EditClosingDecisionWarning;

/** 5.2 lint_before and lint_after. */
export interface EditLintCount {
  ok: boolean;
  count: number;
}

/** A person on a message (the requester or the reviewer). */
export interface EditPerson {
  id: string;
  full_name: string | null;
}

/** Appendix A: the run block carried inside a message summary. */
export interface EditMessageRunSummary {
  status: BuilderRunStatus;
  stage: BuilderStage | null;
  progress: BuilderProgress;
  estimate_cents: number | null;
  planned_cents: number | null;
  /** numeric(10,4) on the run row: may arrive as a string through PostgREST; read it with centsOf. */
  spent_cents: number | string;
  error: string | null;
  error_code: string | null;
  /**
   * Plan 18.5: true only when POST /edits/:id/send would take this message as
   * a retry (the run failed, no cancel was requested, an estimate and a
   * stored plan or extraction exist; the server's one retry rule). The card
   * offers "Try again" only when it is true (WP-E30).
   */
  retryable: boolean;
}

/**
 * Appendix A: the message summary (every list item and every { message }
 * answer). The storage keys of the snapshots are never returned; the hashes are.
 */
export interface EditMessage {
  id: string;
  ordinal: number;
  version_id: string;
  course_id: string;
  run_id: string;
  instruction: string;
  scope: EditScope;
  has_material: boolean;
  outcome: EditOutcome | null;
  /** The planner's words to the admin (at most 600 characters). */
  reply: string | null;
  plan_summary: EditPlanSummary | null;
  diff_summary: EditDiffCounts | null;
  warnings: EditWarning[];
  lint_before: EditLintCount | null;
  lint_after: EditLintCount | null;
  review: EditReview | null;
  reviewed_by: EditPerson | null;
  reviewed_at: string | null;
  /** Set once, with the move to reverted (5.2). */
  revert_reason?: string | null;
  requested_by: EditPerson;
  created_at: string;
  finished_at: string | null;
  /** G24: the draft's content_sha256 when the message was sent. */
  base_sha256?: string;
  /** G24: the draft's content_sha256 the applied message saved. */
  result_sha256?: string | null;
  run: EditMessageRunSummary;
}

/** 5.1: the run mode an edit message's run carries (api/builder.ts BuilderRunMode predates it). */
export type EditRunMode = 'edit';

/** The builder's run summary (Appendix D of the builder plan) for a run of mode 'edit'. */
export type EditRun = Omit<BuilderRun, 'mode'> & { mode: EditRunMode };

/** The { message, run } answer of Send, the second Send and Cancel. */
export interface EditMessageAndRun {
  message: EditMessage;
  run: EditRun;
}

/** Appendix D diff: one changed, added or removed question. */
export interface EditDiffQuestion {
  /** The ordinal after the edit (null for a removed question). */
  ordinal: number | null;
  /** The ordinal before the edit (null for an added question). */
  before_ordinal: number | null;
  section_id: string | null;
  status: 'added' | 'removed' | 'changed';
  changed_fields: string[];
  stem_diff: DiffReadingLine[];
  options_diff: DiffReadingLine[];
}

/** Appendix D diff: the course-level entries' field names. */
export type EditDiffCourseFieldName = EditCourseField | 'quiz_settings' | 'sources';

/** One course-level field's change; EditDiffCourseEntry is the union over the field names. */
export interface EditDiffCourseChange<F extends EditDiffCourseFieldName, V> {
  field: F;
  status: 'changed';
  before: V | null;
  after: V | null;
}

/** Appendix D diff: one course-level field before and after (narrow on `field`). */
export type EditDiffCourseEntry =
  | EditDiffCourseChange<'learning_objectives', string[]>
  | EditDiffCourseChange<'not_covered', string>
  | EditDiffCourseChange<'glossary', GlossaryEntry[]>
  | EditDiffCourseChange<'key_takeaways', string[]>
  | EditDiffCourseChange<'competency_checklist', ChecklistItem[]>
  | EditDiffCourseChange<'quiz_settings', VersionQuizSettings>
  | EditDiffCourseChange<'sources', CourseSource[]>;

/** Appendix D diff: one literal wording swap and how many places it changed. */
export interface EditDiffSwap {
  find: string;
  replace: string;
  count: number;
}

/**
 * Appendix D: the per-message diff, computed once when the message was
 * written (D10). `sections` is exactly courses/diff.js diffContent's shape, so
 * DiffView's section card renders it.
 */
export interface EditDiff {
  version: number;
  sections: DiffSection[];
  questions: EditDiffQuestion[];
  course: EditDiffCourseEntry[];
  swaps: EditDiffSwap[];
  counts: EditDiffCounts;
}

/** GET /admin/builder/edits/:messageId. */
export interface EditMessageDetail {
  message: EditMessage;
  run: EditRun;
  /** The attachments (lms_builder_sources rows of the edit run); empty for a text-only message. */
  sources: BuilderSource[];
  /** Null until the message is applied (and for every outcome without a write). */
  diff: EditDiff | null;
}

/** POST /admin/builder/edits/:messageId/revert: the message and the reloaded draft (GET /admin/versions/:id's body). */
export interface RevertEditResult {
  message: EditMessage;
  version: VersionDetail;
}

/**
 * GET /admin/builder/versions/:versionId/edits/estimate (Appendix A, 16): the
 * upper bound shown before Send, computed by code from the draft's real size.
 */
export interface EditEstimate {
  draft: { sections: number; tokens: number };
  scope: EditScope;
  estimate: {
    plan_cents: number;
    /**
     * Appendix A names the key without fixing its form: one figure, or one per
     * section id. Read it with sectionCentsOf.
     */
    per_section_cents: number | Record<string, number>;
    course_level_cents: number;
    one_section_cents: number;
    scoped_cents: number;
    whole_course_cents: number;
  };
  /** The per-message cap (min of LMS_EDITOR_MESSAGE_CAP_CENTS and the builder run cap, 4.3). */
  cap_cents: number;
  caps: { daily_remaining_cents: number; monthly_remaining_cents: number };
  limits: {
    max_messages: number;
    used_messages: number;
    max_instruction_chars: number;
    max_files: number;
    max_bytes_per_file: number;
    accepted_types: string[];
  };
  models: { plan: string | null; author: string | null };
}

export interface ListEditMessagesQuery {
  limit?: number;
  offset?: number;
}

/** POST /admin/builder/versions/:versionId/edits. */
export interface SendEditMessageInput {
  /** Trimmed before sending; the server refuses a blank one or one over the limit. */
  instruction: string;
  /** Absent, null or kind 'course': the whole course. Kind 'sections' sends its ids (the server refuses an empty list). */
  scope?: EditScope | null;
  files?: File[];
  /** 18.2 Q7 of the builder plan: https links, counted with the files. */
  sourceLinks?: BuilderSourceLink[];
  sourcesMeta?: BuilderSourceMeta[];
  extraSources?: BuilderExtraSource[];
  /** 9: the policy checkbox; sent as 'true' or 'false' on a multipart body (the server refuses anything but 'true'). */
  attestation?: boolean;
}

/** The JSON body of a Send with no attachments. */
export interface SendEditJsonBody {
  instruction: string;
  scope?: { section_ids: string[] };
}

// ---------------------------------------------------------------------------
// Body builders
// ---------------------------------------------------------------------------

function trimmedOrEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Trimmed, de-duplicated, non-blank ids in the given order. */
function cleanIds(ids: readonly string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of ids ?? []) {
    const id = trimmedOrEmpty(raw);
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * The section ids a scope sends, or null for the whole course. A sections
 * scope is never widened to the whole course on the way out: its ids are sent
 * even when empty, so the server refuses the message rather than editing more
 * than the admin chose.
 */
export function scopeSectionIds(scope: EditScope | null | undefined): string[] | null {
  if (!scope || scope.kind !== 'sections') return null;
  return cleanIds(scope.section_ids);
}

/**
 * True when the Send must be multipart: files, links, source facts or extra
 * sources are present (Appendix A's JSON body carries only instruction and scope).
 */
export function editMessageNeedsMultipart(input: SendEditMessageInput): boolean {
  return (
    (input.files?.length ?? 0) > 0 ||
    (input.sourceLinks?.length ?? 0) > 0 ||
    (input.sourcesMeta?.length ?? 0) > 0 ||
    (input.extraSources?.length ?? 0) > 0
  );
}

/** The JSON body of a Send without attachments: { instruction, scope? }. */
export function buildSendEditJson(input: SendEditMessageInput): SendEditJsonBody {
  const body: SendEditJsonBody = { instruction: trimmedOrEmpty(input.instruction) };
  const ids = scopeSectionIds(input.scope);
  if (ids !== null) body.scope = { section_ids: ids };
  return body;
}

/**
 * The multipart body of a Send with attachments (Appendix A): files[] in
 * upload order, instruction, scope as JSON (left out for the whole course),
 * source_links, sources_meta and extra_sources as JSON when present, and
 * attestation 'true' or 'false'.
 */
export function buildSendEditForm(input: SendEditMessageInput): FormData {
  const form = new FormData();
  for (const file of input.files ?? []) form.append('files[]', file);
  form.append('instruction', trimmedOrEmpty(input.instruction));
  const ids = scopeSectionIds(input.scope);
  if (ids !== null) form.append('scope', JSON.stringify({ section_ids: ids }));
  if (input.sourceLinks && input.sourceLinks.length > 0) {
    const links = input.sourceLinks.map((link) => {
      const title = trimmedOrEmpty(link.title);
      return title ? { url: link.url.trim(), title } : { url: link.url.trim() };
    });
    form.append('source_links', JSON.stringify(links));
  }
  if (input.sourcesMeta && input.sourcesMeta.length > 0) {
    form.append('sources_meta', JSON.stringify(input.sourcesMeta));
  }
  if (input.extraSources && input.extraSources.length > 0) {
    form.append('extra_sources', JSON.stringify(input.extraSources));
  }
  form.append('attestation', input.attestation ? 'true' : 'false');
  return form;
}

// ---------------------------------------------------------------------------
// Calls (one per route of Appendix A)
// ---------------------------------------------------------------------------

/** GET /api/lms/admin/builder/versions/:versionId/edits (ordinal ascending; a draft or a published version's history). */
export async function listEditMessages(
  versionId: string,
  query: ListEditMessagesQuery = {}
): Promise<LmsList<EditMessage>> {
  const params: Record<string, number> = {
    limit: Math.min(Math.max(1, query.limit ?? EDITOR_MESSAGES_MAX_LIMIT), EDITOR_MESSAGES_MAX_LIMIT),
  };
  if (query.offset) params.offset = query.offset;
  const response = await api.get<LmsList<EditMessage>>(
    `/api/lms/admin/builder/versions/${enc(versionId)}/edits`,
    { params }
  );
  const data = response.data;
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    page: data?.page ?? { limit: params.limit, offset: params.offset ?? 0, hasMore: false },
  };
}

/**
 * GET /api/lms/admin/builder/versions/:versionId/edits/estimate. A sections
 * scope sends section_ids comma-joined; the whole course (or a sections scope
 * with nothing chosen yet) sends no ids and gets the whole-course figures.
 * 409 LMS_IMMUTABLE on a published version.
 */
export async function getEditEstimate(versionId: string, scope?: EditScope | null): Promise<EditEstimate> {
  const params: Record<string, string> = {};
  const ids = scopeSectionIds(scope);
  if (ids && ids.length > 0) params.section_ids = ids.join(',');
  const response = await api.get<EditEstimate>(
    `/api/lms/admin/builder/versions/${enc(versionId)}/edits/estimate`,
    { params }
  );
  return response.data;
}

/**
 * POST /api/lms/admin/builder/versions/:versionId/edits: JSON { instruction,
 * scope? } for a text-only message, multipart when anything is attached
 * (editMessageNeedsMultipart). 201 { message, run }: the run queued (text
 * only) or uploaded (attachments: the first job extracts and estimates, then
 * startEditMessage starts the edit).
 */
export async function sendEditMessage(versionId: string, input: SendEditMessageInput): Promise<EditMessageAndRun> {
  const url = `/api/lms/admin/builder/versions/${enc(versionId)}/edits`;
  const body = editMessageNeedsMultipart(input) ? buildSendEditForm(input) : buildSendEditJson(input);
  const response = await api.post<EditMessageAndRun>(url, body);
  return response.data;
}

/** GET /api/lms/admin/builder/edits/:messageId (the message, its run, its attachments and its diff). */
export async function getEditMessage(messageId: string): Promise<EditMessageDetail> {
  const response = await api.get<EditMessageDetail>(`/api/lms/admin/builder/edits/${enc(messageId)}`);
  const data = response.data;
  return {
    message: data.message,
    run: data.run,
    sources: Array.isArray(data.sources) ? data.sources : [],
    diff: data.diff ?? null,
  };
}

/**
 * POST /api/lms/admin/builder/edits/:messageId/send: the second Send of a
 * message with attachments (from estimated), or a retry of a failed message
 * whose run is retryable. 409 LMS_CONFLICT (wrong status) or
 * LMS_EDITOR_DRAFT_CHANGED (the draft moved since the first Send; nothing spent).
 */
export async function startEditMessage(messageId: string): Promise<EditMessageAndRun> {
  const response = await api.post<EditMessageAndRun>(`/api/lms/admin/builder/edits/${enc(messageId)}/send`);
  return response.data;
}

/** POST /api/lms/admin/builder/edits/:messageId/cancel (waiting: cancelled at once; running: cancel requested; terminal: 409). */
export async function cancelEditMessage(messageId: string): Promise<EditMessageAndRun> {
  const response = await api.post<EditMessageAndRun>(`/api/lms/admin/builder/edits/${enc(messageId)}/cancel`);
  return response.data;
}

/** POST /api/lms/admin/builder/edits/:messageId/accept (no draft write; 409 LMS_EDITOR_NOT_REVIEWABLE). */
export async function acceptEditMessage(messageId: string): Promise<EditMessage> {
  const response = await api.post<{ message: EditMessage }>(`/api/lms/admin/builder/edits/${enc(messageId)}/accept`);
  return response.data.message;
}

/**
 * POST /api/lms/admin/builder/edits/:messageId/revert { reason? }: restores
 * the draft to the message's before state (6.11). The reason is trimmed and
 * left out when blank; the server refuses one over 500 characters. 409
 * LMS_EDITOR_REVERT_STALE when the draft changed after the message.
 */
export async function revertEditMessage(messageId: string, reason?: string | null): Promise<RevertEditResult> {
  const trimmed = trimmedOrEmpty(reason);
  const body = trimmed ? { reason: trimmed } : {};
  const response = await api.post<RevertEditResult>(`/api/lms/admin/builder/edits/${enc(messageId)}/revert`, body);
  return response.data;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** True for a run of mode 'edit' (accepts the builder's run summary, whose mode type predates 'edit'). */
export function isEditRun(run: { mode?: string | null } | null | undefined): boolean {
  return Boolean(run && run.mode === 'edit');
}

/**
 * One section's upper bound from an estimate: the figure itself when the
 * server sends one number; the section's entry when it sends one per section
 * id; with no id (or an unknown one) the largest entry, so the bound stays an
 * upper bound. 0 when unreadable.
 */
export function sectionCentsOf(estimate: EditEstimate | null | undefined, sectionId?: string | null): number {
  const per: unknown = estimate?.estimate?.per_section_cents;
  if (per === null || per === undefined) return 0;
  if (typeof per !== 'object') return centsOf(per);
  const map = per as Record<string, unknown>;
  if (sectionId && Object.prototype.hasOwnProperty.call(map, sectionId)) return centsOf(map[sectionId]);
  return Object.values(map).reduce<number>((max, value) => Math.max(max, centsOf(value)), 0);
}

// ---------------------------------------------------------------------------
// Error readers (Appendix A codes; editModel.ts maps a code to plain words)
// ---------------------------------------------------------------------------

/**
 * Every editor-specific code a route or an edit run's error_code can carry
 * (Appendix A, 6.2, 6.11, 6.13). The builder codes the edit routes share (the
 * switch, the network allow-list, the files, the spend guards, the queue) are
 * BUILDER_ERROR_CODES in api/builder.ts.
 */
export const EDITOR_ERROR_CODES = [
  // G25, 18.2 Q15: the off-lever reads 'false' (503 on every edit route; a run refused on the worker).
  'LMS_EDITOR_DISABLED',
  // 6.2: the draft holds its maximum number of messages (429).
  'LMS_EDITOR_MESSAGE_LIMIT',
  // 6.11: accept or revert on a message that is not applied, or already reverted (409).
  'LMS_EDITOR_NOT_REVIEWABLE',
  // G24: the draft changed after the message, so it cannot be reverted (409).
  'LMS_EDITOR_REVERT_STALE',
  // 6.11: a revert whose saved hash did not match the base hash (500, logged with the code).
  'LMS_EDITOR_REVERT_MISMATCH',
  // G24: the draft changed after Send (409 at the second Send; a run error at plan or write time).
  'LMS_EDITOR_DRAFT_CHANGED',
  // 6.13: run error codes, none retried.
  'LMS_EDITOR_MESSAGE_MISSING',
  'LMS_EDITOR_PLAN_INVALID',
  'LMS_EDITOR_SCOPE_VIOLATION',
] as const;

export type EditorErrorCode = (typeof EDITOR_ERROR_CODES)[number];

/** True when `code` is one of the editor's own codes. */
export function isEditorErrorCode(code: unknown): code is EditorErrorCode {
  return typeof code === 'string' && (EDITOR_ERROR_CODES as readonly string[]).includes(code);
}

/** A failed editor call, read once: HTTP status, code and the server's message. */
export type EditorApiError = BuilderApiError;

/** Read an axios-shaped rejection into { status, code, message }; `fallback` when the server sent no message. */
export function readEditorError(err: unknown, fallback = 'Edit with AI could not complete the request.'): EditorApiError {
  return readBuilderError(err, fallback);
}

/**
 * The editor is off (503 LMS_EDITOR_DISABLED, or LMS_BUILDER_DISABLED because
 * the editor needs the builder's switch too): the screen should hide the
 * panel, not show a failure.
 */
export function isEditorDisabledError(err: unknown): boolean {
  const code = apiErrorCode(err);
  return code === 'LMS_EDITOR_DISABLED' || code === 'LMS_BUILDER_DISABLED';
}
